import React from 'react';
import s from './s.module.less';
import PPAOnboradingHeader from 'components/PPAOnboardingHeader';
import LoginForm from './components/LoginForm';
import SceneArt from './imgs/provider-signin-scene.png';
import type { LoginFormData } from './types';
import { PATH } from 'constants/path';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserToken, setLocalStoreProviderTokenSetTime } from 'utils/localstore';
import { setCookieProviderToken, setCookieProviderTokenSetTime } from 'utils/shareAccount';
import { ENV_VAR } from 'constants/access';
import { getCurrentUser } from 'api/common';
import { getHomeInfo } from 'api/provider';
import { getStep } from 'api/ppaOnboarding';
import { checkLeadFormByEmail } from 'api/ppaLeadForm';
import { getIntentRedirect } from 'pages/PPALeadFormPage/getIntentRedirect';
import { INVITED_STEP_ROUTE, isInvitedProviderStep } from 'pages/InvitedProviderOnboarding/steps';
import { ERoleCode } from 'types/enumItems';
import { getUrlParam } from 'utils/common';
import { isPPAOnboardingCompleted } from 'utils/common';

/**
 * 左侧价值主张卖点（移植自 helloklarity provider-signup 设计）
 * 每项由一个蓝色对勾图标 + 粗体小标题 + 灰色一行说明组成。
 */
const FEATURES = [
    {
        title: 'Credentialing and billing, done for you.',
        body: 'Your own NPI, with insurance billed at a flat 3.9%.',
    },
    {
        title: 'Patient marketing, done for you.',
        body: 'Active providers see 20+ new patients a month.',
    },
    {
        title: 'Essential practice software, done for you.',
        body: 'Telehealth, charting, e-Rx, and payments in one EHR.',
    },
];

/**
 * PPA登录页面
 * 包含左侧信任内容展示、右侧登录表单以及顶部导航
 */
export default function PPALoginPage() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [redirect, setRedirect] = React.useState(getUrlParam('redirect') || PATH.DASHBOARD);
    console.log({
        redirect,
    });
    /**
     * 处理表单提交
     * @param values 表单数据
     */
    const handleFormSubmit = async (values: LoginFormData) => {
        localStorage.setItem(ENV_VAR, '');
        try {
            const token = getUserToken();
            if (token) {
                setLocalStoreProviderTokenSetTime(new Date().getTime());
                setCookieProviderToken(token);
                setCookieProviderTokenSetTime(new Date().getTime());
            }
        } catch (error) {
            console.error(error);
        }

        // Fetch fresh user info + permissions, then route to the correct onboarding
        // entry based on groupPractice flag. The entry page itself handles
        // step-based resume / completion redirects.
        try {
            const userRes = await getCurrentUser();
            const userData = userRes?.data?.data;
            const roleCode = userData?.roleCode;
            const isGroupPractice = roleCode === ERoleCode.GROUP_PROVIDER;
            const isAssistantRole =
                roleCode === ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT ||
                roleCode === ERoleCode.GROUP_CLINICAL_ASSISTANT;

            if (isAssistantRole) {
                navigate(`${PATH.DASHBOARD}/clients`);
                return;
            }

            if (isGroupPractice) {
                const homeRes = await getHomeInfo();
                if (isPPAOnboardingCompleted(homeRes?.data?.data)) {
                    navigate(redirect);
                } else {
                    const stepRes = await getStep();
                    const savedStep = stepRes?.data?.data?.currentStep;
                    const target = isInvitedProviderStep(savedStep)
                        ? INVITED_STEP_ROUTE[savedStep]
                        : PATH.INVITED_PROVIDER_ONBOARDING_CREATE_PROFILE;
                    navigate(target);
                }
                return;
            }
        } catch (error) {
            console.error(error);
        }

        try {
            const leadRes = await checkLeadFormByEmail({ email: values.email });
            const practiceHelpType = leadRes?.data?.data?.leadForm?.practiceHelpType;
            if (practiceHelpType) {
                const target = await getIntentRedirect(practiceHelpType);
                navigate(target);
                return;
            }
        } catch (error) {
            console.error(error);
        }

        navigate(redirect);
    };

    // 跳转到注册（PPA lead form），对应右侧 "Create a provider account"
    const handleSignUp = () => {
        navigate(PATH.PPA_LEAD_FORM);
    };

    return (
        <div className={s.wrap}>
            <PPAOnboradingHeader />

            {/* 左右分栏（移植自 helloklarity provider-signup 设计）：
                左侧蓝底价值主张 + 底部医生插图；右侧白底登录表单。 */}
            <div className={s.hero}>
                {/* ===== 左侧：价值主张 ===== */}
                <div className={s.left}>
                    <h1 className={s.leftTitle}>Spend your time on patients, not paperwork.</h1>
                    <p className={s.leftIntro}>True independence — your name and reputation are yours to keep.</p>
                    <div className={s.features}>
                        {FEATURES.map((f) => (
                            <div className={s.feature} key={f.title}>
                                <svg className={s.featureCheck} width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M5 13L9 17L19 7"
                                        stroke="#0E62F4"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div>
                                    <h3 className={s.featureTitle}>{f.title}</h3>
                                    <p className={s.featureBody}>{f.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className={s.leftClose}>You did the hard part. Let us handle the rest.</p>
                    <img className={s.heroArt} src={SceneArt} alt="Illustration of a Klarity provider at a laptop" />
                </div>

                {/* ===== 右侧：登录表单 ===== */}
                <div className={s.right}>
                    <h2 className={s.title}>Log in to your provider dashboard</h2>
                    <p className={s.bookingHint}>
                        Use the email you registered with Klarity to access your clinician dashboard.
                    </p>

                    {/* 真实登录表单，隐藏其内置标题，改用上方 provider-signup 风格标题 */}
                    <LoginForm hideHeader onSubmit={handleFormSubmit} />

                    <p className={s.supportText}>
                        Questions or trouble signing in? Email us at{' '}
                        <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a> and
                        we&rsquo;ll get back to you shortly.
                    </p>

                    <div className={s.switchDivider}>
                        <span>New to Klarity?</span>
                    </div>
                    <button type="button" className={s.switchBtn} onClick={handleSignUp}>
                        Create a provider account
                    </button>
                </div>
            </div>
        </div>
    );
}
